/**
 * Cart Management System - Moon Moon Agency v2.1
 * Shopify cart with AJAX updates, custom events, and UI sync
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CART_CONFIG = {
	// API Settings
	debounceDelay: 500,
	apiTimeout: 10000,

	// Selectors
	selectors: {
		cart: '[data-cart], .mm-cart',
		cartWrapper: '[data-cart-wrapper]',
		cartProducts: '[data-cart-products]',
		cartBubbles: '[data-cart-count-bubble]',
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: '[data-cart-close]',
		checkoutBtn: '[data-checkout-btn]',
		drawerCart: '[data-drawer-cart]',
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: '[data-item-quantity]',
		freeProducts: '[data-free-products]'
	},

	// Development mode (uses global isDevelopment from scripts.liquid)
	isDevelopment: typeof isDevelopment !== 'undefined' ? isDevelopment : false
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Logging system
const cartLog = (level, message, data = {}) => {
	if (!CART_CONFIG.isDevelopment && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		logFn(`🛒 [CART] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

// Debounce function
// Use global debounce from mm_utils.js
const cartDebounce = window.debounce || ((fn, delay) => {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => fn.apply(this, args), delay);
	};
});

// Cart Event Emitter
const cartEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'cart'
	};
	
	return document.dispatchEvent(new CustomEvent(`cart:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const DOM = {
	get cart() { return document.querySelector(CART_CONFIG.selectors.cart); },
	get cartSection() { return document.getElementById(this.cart?.id || ''); },
	get cartProducts() { return this.cartSection?.querySelector(CART_CONFIG.selectors.cartProducts); },
	get cartWrapper() { return document.querySelector(CART_CONFIG.selectors.cartWrapper); },
	get cartBubbles() { return document.querySelectorAll(CART_CONFIG.selectors.cartBubbles); },
	
	cartItem: (index) => document.querySelector(CART_CONFIG.selectors.lineIndex.replace('{{index}}', index)),
	
	cartQuantity: (index) => DOM.cartItem(index)?.querySelector(CART_CONFIG.selectors.itemQuantity)
};

/*********************************************************************************************
 * 🔌 CORE CART API
 */
const CartAPI = {
	async request(method, path, body = null) {
		cartEmit('api-start', { method, path });

		const baseUrl = window.Shopify?.routes?.root || '/';
		const url = baseUrl + path;

		const isFormData = (typeof FormData !== 'undefined') && body instanceof FormData;

		const config = { method };

		// 👉 IMPORTANT : n'ajoute pas Content-Type si c'est du FormData
		if (!isFormData) {
			config.headers = { 'Content-Type': 'application/json' };
		}

		if (body && method !== 'GET') {
			config.body = isFormData ? body : JSON.stringify(body);
		}

		try {
			const response = await fetch(url, config);
			if (!response.ok) {
				// 422 (stock, quantite max...) : Shopify renvoie un message traduit dans description
				let description = '';
				try { description = (await response.json()).description || ''; } catch (e) {}
				const httpError = new Error(description || `HTTP ${response.status}: ${response.statusText}`);
				httpError.userMessage = description;
				throw httpError;
			}

			const data = await response.json();

			if (data.status && data.status >= 400) {
				throw new Error(data.message || data.description || 'Shopify API Error');
			}

			// Sections demandees (corps JSON, FormData ou ?sections= du GET) : completees par
			// ensureSections si l'API panier ne les renvoie pas (apercu admin)
			const sectionIds = isFormData ? body.get('sections') : body?.sections || new URLSearchParams(path.split('?')[1] || '').get('sections');
			if (sectionIds) await this.ensureSections(data, sectionIds);

			cartEmit('api-success', { method, path, itemCount: data.item_count });
			this.updateUI(data);
			return data;
		} catch (error) {
			cartEmit('api-error', { method, path, error: error.message });
			throw error;
		}
	},

	/**
	 * Complete data.sections quand l'API panier ne les renvoie pas.
	 * Dans l'apercu admin d'un theme non publie, cart/add.js, change.js et update.js
	 * renvoient sections: null et cart.js?sections= n'en renvoie aucune : seule l'API
	 * de rendu de sections sur une page (/?sections=) fonctionne. Aucune requete en
	 * plus sur le site en ligne, ou les sections sont deja dans la reponse.
	 * @param {Object} data - Reponse JSON de l'API panier, completee sur place
	 * @param {string|string[]} ids - Id(s) de section demandes
	 * @returns {Promise<Object>} data
	 */
	async ensureSections(data, ids) {
		const list = (Array.isArray(ids) ? ids : String(ids).split(',')).filter(Boolean);
		if (!data || !list.length || list.every(id => data.sections?.[id])) return data;

		try {
			// routes.root garde le prefixe de langue de l'URL
			const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${list.join(',')}`, {
				signal: AbortSignal.timeout(CART_CONFIG.apiTimeout)
			});
			if (response.ok) data.sections = { ...data.sections, ...(await response.json()) };
		} catch (error) {
			cartLog('warn', 'Sections fallback failed', { error: error.message });
		}
		return data;
	},

	/**
	 * Updates cart UI elements with new data
	 */
	updateUI(data) {
		// Update cart count
		const count = data.item_count || 0;
		DOM.cartBubbles.forEach(bubble => {
			bubble.textContent = count;
		});
		cartEmit('count-updated', { count });

		// Update cart content if sections exist
		if (data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace('shopify-section-', '');
			if (data.sections[sectionId]) {
				this.refreshDOM(data.sections[sectionId]);
			}
		}
	},

	/**
	 * Refreshes cart DOM with new HTML and reinitializes components
	 */
	refreshDOM(html) {
		const temp = document.createElement('div');
		temp.innerHTML = html;

		const newCart = temp.querySelector(CART_CONFIG.selectors.drawerCart);
		const currentCart = DOM.cartSection?.querySelector(CART_CONFIG.selectors.drawerCart);

		if (newCart && currentCart) {
			// Focus dans le tiroir (bouton +/-, suppression...) : on le retrouvera apres le re-rendu
			// par ses attributs data-*, sinon il tomberait sur body (WCAG 2.4.3)
			const active = document.activeElement;
			let focusSelector = null;
			if (active && active !== currentCart && currentCart.contains(active)) {
				const dataAttrs = Array.from(active.attributes).filter(a => a.name.startsWith('data-'));
				focusSelector = active.tagName.toLowerCase() + dataAttrs.map(a => `[${a.name}="${CSS.escape(a.value)}"]`).join('');
			}

			// Destroy existing Splide instances before replacing DOM
			if (typeof Splide !== 'undefined') {
				const existingSplides = document.querySelectorAll('.mm-drawer-cart .splide');
				cartLog('info', `Destroying ${existingSplides.length} existing Splide instances`);
				existingSplides.forEach(function(element) {
					// Try to destroy using the Splide API
					if (element.splide) {
						try {
							cartLog('info', 'Destroying Splide instance via API', { className: element.className });
							element.splide.destroy();
						} catch (error) {
							cartLog('warn', 'Failed to destroy Splide via API:', { element, error });
						}
					}

					// Also manually remove pagination elements as fallback
					const pagination = element.querySelector('.splide__pagination');
					if (pagination) {
						try {
							cartLog('info', 'Manually removing pagination', { className: element.className });
							pagination.remove();
						} catch (error) {
							cartLog('warn', 'Failed to remove pagination:', { element, error });
						}
					}
				});
			}

			currentCart.innerHTML = newCart.innerHTML;

			// Reinitialize Splide sliders
			if (typeof Splide !== 'undefined') {
				const newSplides = document.querySelectorAll('.mm-drawer-cart .splide');
				cartLog('info', `Initializing ${newSplides.length} new Splide instances`);
				newSplides.forEach(function(element) {
					try {
						cartLog('info', 'Mounting new Splide instance', { className: element.className });
						// Store instance on element for later destruction
						element.splide = new Splide(element).mount();
					} catch (error) {
						cartLog('warn', 'Failed to initialize Splide:', { element, error });
					}
				});
			}
			// Reinitialize event listeners for new DOM elements
			if (typeof window.initAddToCartForms === 'function') {
				window.initAddToCartForms();
			}
			if (typeof window.initCartQuantityListeners === 'function') {
				window.initCartQuantityListeners();
			}
			if (focusSelector) {
				let target = null;
				try { target = currentCart.querySelector(focusSelector); } catch (e) {}
				(target || currentCart).focus({ preventScroll: !!target });
			}
			// Reinitialize promo code listeners
			if (typeof initPromoCode === 'function') {
				initPromoCode();
			}
			// Reinitialize club toggle
			if (typeof initClubToggle === 'function') {
				initClubToggle();
			}
			// Reinitialize club benefits popup
			if (typeof initClubBenefitsPopup === 'function') {
				initClubBenefitsPopup();
			}
			// Reinitialize checkout button
			if (typeof initCheckoutButton === 'function') {
				initCheckoutButton();
			}
			// Reapply club pricing after DOM refresh
			if (typeof applyClubPricing === 'function') {
				// Use longer timeout to ensure cross-sell is loaded (Splide carousel needs time)
				setTimeout(() => {
					applyClubPricing();
				}, 800);
			}
			// Reinitialize shipping estimate
			if (typeof window.initShippingEstimate === 'function') {
				window.initShippingEstimate();
			}
			// Load cross-sell recommendations
			if (typeof initCrossSellRecommendations === 'function') {
				initCrossSellRecommendations();
			}
			cartEmit('refreshed');
		}
	}
};

/*********************************************************************************************
 * 🛒 CART DATA MANAGEMENT
 */
const Cart = {
	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		container: null,
		handleKeyDown: null,
		handleFocusIn: null
	},

	/**
	 * Elements atteignables au Tab dans le tiroir, recalcules a chaque appui :
	 * le contenu change (refreshDOM, puces Splide en autoplay, cross-sell charge apres).
	 */
	getTabbables(container) {
		return Array.from(container.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]'
		)).filter(el => el.tabIndex >= 0 && (typeof el.checkVisibility !== 'function' || el.checkVisibility({ visibilityProperty: true })));
	},

	/**
	 * Setup focus trap for cart drawer
	 */
	setupFocusTrap(cartWrapper, triggerElement) {
		// Deja actif (ouverture depuis le tiroir, reouverture) : on garde le declencheur d'origine
		if (this.focusTrap.isActive) {
			if (triggerElement && !cartWrapper.contains(triggerElement)) this.focusTrap.triggerElement = triggerElement;
			return;
		}

		this.focusTrap.triggerElement = triggerElement || null;
		this.focusTrap.container = cartWrapper;

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes cart
			if (e.key === 'Escape' || e.key === 'Esc') {
				e.preventDefault();
				this.close(); // rend le focus au declencheur
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			const tabbables = this.getTabbables(cartWrapper);
			if (!tabbables.length) { e.preventDefault(); return; }
			const first = tabbables[0];
			const last = tabbables[tabbables.length - 1];
			const inside = cartWrapper.contains(document.activeElement);

			if (e.shiftKey) {
				// Premier element, conteneur du tiroir (tabindex -1) ou hors du tiroir : on reboucle a la fin
				if (!inside || document.activeElement === first || !tabbables.includes(document.activeElement)) {
					e.preventDefault();
					last.focus();
				}
			} else if (!inside || document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};

		// Garde : un focus qui sort du tiroir ouvert y est ramene (Safari saute les
		// boutons au Tab, le rebouclage sur le dernier element ne suffit pas)
		this.focusTrap.handleFocusIn = (e) => {
			if (cartWrapper.contains(e.target)) return;
			const tabbables = this.getTabbables(cartWrapper);
			(tabbables[0] || cartWrapper.querySelector('[data-drawer-cart]'))?.focus();
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		document.addEventListener('focusin', this.focusTrap.handleFocusIn);
		this.focusTrap.isActive = true;

		// Focus the drawer container (tabindex="-1") to avoid visible outline on interactive elements
		// User can then Tab to reach the first interactive element
		const drawerCart = cartWrapper.querySelector('[data-drawer-cart]');
		setTimeout(() => (drawerCart || this.getTabbables(cartWrapper)[0])?.focus(), 100);

		cartLog('info', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			document.removeEventListener('focusin', this.focusTrap.handleFocusIn);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.container = null;
			this.focusTrap.handleKeyDown = null;
			this.focusTrap.handleFocusIn = null;
			cartLog('info', 'Focus trap removed');
		}
	},

	/**
	 * Erreur visible et annoncee (A11Y-14, WCAG 3.3.1 / 4.1.3) : dans le tiroir s'il est
	 * ouvert, sinon apres le formulaire du bouton d'ajout. Masquee apres quelques secondes.
	 */
	announceError(message, anchor = null) {
		const wrapper = DOM.cartWrapper;
		const text = message || wrapper?.dataset.cartErrorText || '';
		if (!text) return;

		document.querySelectorAll('[data-cart-alert]').forEach(el => el.remove());
		const alert = document.createElement('p');
		alert.setAttribute('role', 'alert');
		alert.setAttribute('data-cart-alert', '');
		alert.textContent = text;

		const isOpen = wrapper?.classList.contains('mm-dc-is-open');
		const container = isOpen ? wrapper.querySelector('.mm-dc-content-container') || wrapper.querySelector('[data-drawer-cart]') : null;
		// Fiche produit : juste sous le bloc quantite + bouton (le formulaire englobe d'autres blocs)
		const target = anchor?.closest('.mm-product-form-component') || anchor?.closest('form') || anchor;
		if (container) container.prepend(alert);
		else if (target?.isConnected) target.after(alert);
		else return;

		clearTimeout(this._alertTimer);
		this._alertTimer = setTimeout(() => alert.remove(), 8000);
	},

	async get() {
		cartEmit('fetch-start');
		const data = await CartAPI.request('GET', 'cart.js');
		cartEmit('fetched', { itemCount: data.item_count, totalPrice: data.total_price });
		return data;
	},

	/**
	 * Traitement initial des produits offerts (au chargement de la page).
	 *
	 * processFreeProducts() n'est sinon appelé qu'après une action panier (add/update).
	 * Si un palier est DÉJÀ atteint au chargement (ex. 10 produits déclencheurs déjà
	 * dans le panier), il faut traiter le cadeau auto / le nettoyage immédiatement.
	 * On fetch avec ?sections pour que processFreeProducts dispose du HTML rendu.
	 */
	async initFreeProducts() {
		const freeProductsEl = document.querySelector(CART_CONFIG.selectors.freeProducts);
		if (!freeProductsEl) return;

		// On traite au chargement si :
		//  - un palier est débloqué (ajout auto mono-produit, ou nettoyage des doublons), OU
		//  - un cadeau éligible est présent alors que son palier est retombé (retrait auto).
		let needsProcessing = false;
		Array.from(freeProductsEl.children).forEach(el => {
			if (el.dataset.freeProduct === 'true') { needsProcessing = true; return; }
			const eligible = (el.dataset.freeProductVariants || '').split(',').map(s => s.trim()).filter(Boolean);
			const orphanPresent = eligible.some(id =>
				!!document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', id))
			);
			if (orphanPresent) needsProcessing = true;
		});
		if (!needsProcessing) return;

		try {
			// GET cart.js (pour data.items) ; processFreeProducts lira les paliers
			// depuis le DOM live (le GET ne renvoie pas `sections`).
			const data = await CartAPI.request('GET', 'cart.js');
			await this.processFreeProducts(data);
		} catch (error) {
			cartLog('error', 'Initial free products processing failed', { error: error.message });
		}
	},

	/**
	 * Open cart
	 */
	open(triggerElement) {
		if (!cartEmit('before-open', {}, true)) return;

		this.get().catch(error => cartLog('error', 'Failed to fetch cart on open', { error: error.message }));

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper && html) {
			wrapper.classList.add('mm-dc-is-open');
			html.classList.add('stop-scrolling');

			// Hide overlays
			['shopify-chat', 'axeptio_overlay'].forEach(id => {
				const elem = document.getElementById(id);
				if (elem) elem.style.display = 'none';
			});

			// Initialize Splide sliders
			if (typeof Splide !== 'undefined') {
				document.querySelectorAll('.mm-drawer-cart .splide').forEach(function(element) {
					if (!element.splide) {
						try {
							// Store instance on element for later destruction
							element.splide = new Splide(element).mount();
						} catch (error) {
							cartLog('warn', 'Failed to initialize Splide on open:', { element, error });
						}
					}
				});
			}

			// Initialize shipping estimate
			if (typeof window.initShippingEstimate === 'function') {
				window.initShippingEstimate();
			}

			// Load cross-sell recommendations
			if (typeof initCrossSellRecommendations === 'function') {
				initCrossSellRecommendations();
			}

			// Setup focus trap
			this.setupFocusTrap(wrapper, triggerElement);

			cartEmit('opened');
		}
	},

	/**
	 * Close cart
	 */
	close() {
		if (!cartEmit('before-close', {}, true)) return;

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper) wrapper.classList.remove('mm-dc-is-open');
		if (html) html.classList.remove('stop-scrolling');

		// Focus rendu au declencheur (WCAG 2.4.3), lu avant removeFocusTrap qui le remet a null.
		// Seulement si le focus est dans le panier : une fermeture par script ne le deplace pas.
		const trigger = this.focusTrap.triggerElement;
		// Focus perdu sur body (re-rendu) : on le rend aussi au declencheur
		const focusInCart = wrapper && (wrapper.contains(document.activeElement) || document.activeElement === document.body);

		// Remove focus trap
		this.removeFocusTrap();
		if (focusInCart && trigger?.isConnected) trigger.focus();

		// Show overlays
		['shopify-chat', 'axeptio_overlay'].forEach(id => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = '';
		});

		cartEmit('closed');
	},

	/**
	 * Get quantity
	 */
	getQuantity(lineIndex) {
		const elem = DOM.cartQuantity(lineIndex);
		return elem ? parseInt(elem.dataset.itemQuantity, 10) || 0 : 0;
	},

	/**
	 * Extract comprehensive item data from cart DOM
	 */
	getItemData(lineIndex) {
		const itemEl = DOM.cartItem(lineIndex);
		if (!itemEl) return null;

		const data = {
			lineIndex: parseInt(lineIndex, 10),
			variantId: itemEl.dataset.cartItemId,
			quantity: this.getQuantity(lineIndex),
			maxQuantity: itemEl.dataset.maxQuantity,
			properties: {},
			variants: {},
			metadata: {}
		};

		// Extract properties from DOM elements (if displayed)
		const propertyElements = itemEl.querySelectorAll('[data-property]');
		propertyElements.forEach(el => {
			const propName = el.dataset.property;
			const propValue = el.textContent.trim();
			if (propName && propValue) {
				data.properties[propName] = propValue;
			}
		});

		// Extract variant options (if displayed)
		const variantElements = itemEl.querySelectorAll('[data-variant-option]');
		variantElements.forEach(el => {
			const optionName = el.dataset.variantOption;
			const optionValue = el.textContent.trim();
			if (optionName && optionValue) {
				data.variants[optionName] = optionValue;
			}
		});

		// Add any other metadata from data attributes
		Object.keys(itemEl.dataset).forEach(key => {
			if (!['cartItemId', 'lineIndex', 'maxQuantity'].includes(key)) {
				data.metadata[key] = itemEl.dataset[key];
			}
		});

		return data;
	},

	/**
	 * Set quantity
	 */
	setQuantity(lineIndex, newQty, skipEvent = false) {
		const elem = DOM.cartQuantity(lineIndex);
		if (!elem || newQty < 0) return;

		const oldQty = parseInt(elem.dataset.itemQuantity, 10) || 0;
		elem.dataset.itemQuantity = newQty;
		elem.textContent = newQty;

		if (!skipEvent) {
			cartEmit('quantity-changed', { lineIndex, oldQty, newQty });
		}
	},

	/**
	 * Update quantity
	 */
	async updateQuantity(lineIndex, newQty) {
		const itemEl = DOM.cartItem(lineIndex);
		const variantId = itemEl?.dataset.cartItemId;

		cartEmit('quantity-sync-start', { lineIndex, quantity: newQty, variantId });

		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

			// Use cart/update.js with variant ID instead of line index for more reliable updates
			const requestBody = {
				updates: {
					[variantId]: newQty
				}
			};

			// Add sections for bundled section rendering if available
			if (sectionId) {
				requestBody.sections = sectionId;
			}

			const data = await CartAPI.request('POST', 'cart/update.js', requestBody);

			await this.processFreeProducts(data);
			cartEmit('quantity-synced', { lineIndex, quantity: newQty, variantId });
			return data;
		} catch (error) {
			cartEmit('quantity-sync-error', { lineIndex, error: error.message });
			this.announceError(error.userMessage);
			// Fallback: refresh cart to sync with server state
			this.get().catch(error => cartLog('error', 'Failed to fetch cart on open', { error: error.message }));
			throw error;
		}
	},

	/**
	 * Process free products
	 */
	async processFreeProducts(data) {
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		// Source des paliers : le HTML re-rendu (POST add/update qui renvoie `sections`),
		// sinon le DOM live (cas du chargement initial — GET cart.js ne renvoie pas `sections`).
		let freeProductsEl;
		if (data.sections?.[sectionId]) {
			const temp = document.createElement('div');
			temp.innerHTML = data.sections[sectionId];
			freeProductsEl = temp.querySelector(CART_CONFIG.selectors.freeProducts);
		} else {
			freeProductsEl = document.querySelector(CART_CONFIG.selectors.freeProducts);
		}

		if (!freeProductsEl?.children.length) return data;

		/*
		 * Deux cas selon le nombre de cadeaux configurés sur le palier :
		 *
		 *  - MONO-PRODUIT (data-free-product-single-variant présent) : ajout/retrait
		 *    AUTOMATIQUE. Si le palier est atteint et le cadeau absent -> on l'ajoute ;
		 *    s'il n'est plus atteint -> on le retire. Aucun encart côté client.
		 *
		 *  - MULTI-PRODUITS : le client choisit lui-même son cadeau (cf.
		 *    mm-cart-free-product-selector). Ici on ne fait QUE le nettoyage :
		 *      - palier non atteint -> on retire tous les cadeaux présents
		 *      - plusieurs cadeaux présents (changement de choix raté) -> on n'en garde qu'un
		 *    On n'ajoute jamais automatiquement en multi-produits.
		 */
		const updates = {};
		const additions = [];

		Array.from(freeProductsEl.children).forEach(el => {
			let triggered = el.dataset.freeProduct === 'true';

			// Client hors des tags ciblés de l'offre : jamais déclenché,
			// les cadeaux présents sont retirés plus bas.
			const eligible = el.dataset.freeProductEligible !== 'false';

			// Client-side quantity trigger check (safety net)
			const triggerProductId = el.dataset.triggerProductId;
			const triggerQuantity = parseInt(el.dataset.triggerQuantity) || 0;

			if (eligible && triggerProductId && triggerQuantity > 0 && data.items) {
				const triggerQtyInCart = data.items
					.filter(item => String(item.product_id) === String(triggerProductId))
					.reduce((sum, item) => sum + item.quantity, 0);

				if (triggerQtyInCart >= triggerQuantity) {
					triggered = true;
				}
			}

			// Variants offerts éligibles pour ce palier
			const eligibleVariants = (el.dataset.freeProductVariants || '')
				.split(',')
				.map(id => id.trim())
				.filter(Boolean);

			// Variants offerts actuellement dans le panier
			const presentVariants = eligibleVariants.filter(variantId =>
				!!document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', variantId))
			);

			// Mono-produit : variant à ajouter automatiquement (sans encart de sélection)
			const singleVariantId = el.dataset.freeProductSingleVariant;

			if (!triggered) {
				// Palier non atteint -> retirer tous les cadeaux présents
				presentVariants.forEach(variantId => {
					updates[variantId] = 0;
					cartEmit('free-product-removing', { variantId });
				});
			} else if (singleVariantId && presentVariants.length === 0) {
				// Mono-produit + palier atteint + cadeau absent -> ajout automatique
				additions.push(singleVariantId);
				cartEmit('free-product-adding', { variantId: singleVariantId });
			} else if (presentVariants.length > 1) {
				// Sécurité : ne garder que le premier cadeau choisi
				presentVariants.slice(1).forEach(variantId => {
					updates[variantId] = 0;
					cartEmit('free-product-removing', { variantId });
				});
			}
		});

		try {
			// 1. Retraits (cleanup) — un seul cart/update.js
			if (Object.keys(updates).length > 0) {
				const requestBody = { updates };
				if (sectionId) requestBody.sections = sectionId;

				await CartAPI.request('POST', 'cart/update.js', requestBody);
			}

			// 2. Ajouts automatiques (mono-produit) — cart/add.js par variant
			for (const variantId of additions) {
				const formData = new FormData();
				formData.append('id', variantId);
				formData.append('quantity', '1');
				if (sectionId) formData.append('sections', sectionId);

				await CartAPI.request('POST', 'cart/add.js', formData);
			}

			if (Object.keys(updates).length > 0 || additions.length > 0) {
				cartEmit('free-products-updated', { updates, additions });
			}
		} catch (error) {
			cartEmit('free-products-error', { error: error.message });
		}

		return data;
	}
};

/*********************************************************************************************
 * 🎁 FREE PRODUCT SELECTOR — Modal de choix du cadeau
 */
const FreeProductSelector = {
	selectors: {
		open: '[data-fp-open]',
		close: '[data-fp-close]',
		modal: '[data-fp-modal]',
		card: '[data-fp-card]',
		variant: '[data-fp-variant]',
		confirm: '[data-fp-confirm]',
		freeProducts: '[data-free-products]'
	},

	state: {
		selectedVariantId: null,
		lastTrigger: null
	},

	/**
	 * Renvoie tous les variant IDs offerts éligibles (paliers déclenchés)
	 */
	getEligibleVariants() {
		const root = document.querySelector(this.selectors.freeProducts);
		if (!root) return [];

		const ids = [];
		Array.from(root.children).forEach(el => {
			if (el.dataset.freeProduct !== 'true') return;
			(el.dataset.freeProductVariants || '')
				.split(',')
				.map(id => id.trim())
				.filter(Boolean)
				.forEach(id => ids.push(id));
		});
		return ids;
	},

	open(trigger) {
		const modal = document.querySelector(this.selectors.modal);
		if (!modal) return;

		this.state.lastTrigger = trigger || document.querySelector(this.selectors.open);

		modal.classList.add('mm-open');
		modal.setAttribute('aria-hidden', 'false');
		if (this.state.lastTrigger) this.state.lastTrigger.setAttribute('aria-expanded', 'true');

		// Pré-sélectionner le cadeau déjà présent dans le panier (modification)
		this._preselectCurrent(modal);

		// Focus sur le premier élément interactif
		const firstFocusable = modal.querySelector('[data-fp-close]');
		if (firstFocusable) firstFocusable.focus();

		cartEmit('free-product-modal-opened');
	},

	/**
	 * Pré-coche la variante du cadeau déjà choisi (si présent dans le panier)
	 */
	_preselectCurrent(modal) {
		const present = this.getEligibleVariants().find(id =>
			!!document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', id))
		);
		if (!present) return;

		const button = modal.querySelector(`${this.selectors.variant}[data-fp-variant-id="${present}"]`);
		if (button && !button.disabled) this.selectVariant(button);
	},

	close() {
		const modal = document.querySelector(this.selectors.modal);
		if (!modal) return;

		modal.classList.remove('mm-open');
		modal.classList.remove('mm-dc-fp-modal--has-selection');
		modal.setAttribute('aria-hidden', 'true');

		const trigger = this.state.lastTrigger || document.querySelector(this.selectors.open);
		if (trigger) {
			trigger.setAttribute('aria-expanded', 'false');
			trigger.focus();
		}

		cartEmit('free-product-modal-closed');
	},

	/**
	 * Sélection d'une variante (pill ou bouton "Choisir")
	 */
	selectVariant(button) {
		const modal = document.querySelector(this.selectors.modal);
		if (!modal) return;

		// Reset des autres sélections (un seul cadeau possible)
		modal.querySelectorAll(this.selectors.variant).forEach(el => {
			el.classList.remove('mm-selected');
			if (el.getAttribute('role') === 'radio') el.setAttribute('aria-checked', 'false');
		});
		modal.querySelectorAll(this.selectors.card).forEach(card => card.classList.remove('mm-selected'));

		button.classList.add('mm-selected');
		if (button.getAttribute('role') === 'radio') button.setAttribute('aria-checked', 'true');
		button.closest(this.selectors.card)?.classList.add('mm-selected');

		this.state.selectedVariantId = button.dataset.fpVariantId || null;

		// Activer + révéler le CTA (slide-in depuis le bas)
		const confirm = modal.querySelector(this.selectors.confirm);
		if (confirm) confirm.disabled = !this.state.selectedVariantId;
		modal.classList.toggle('mm-dc-fp-modal--has-selection', !!this.state.selectedVariantId);
	},

	/**
	 * Confirme le choix : ajoute le variant offert (remplace l'ancien cadeau si besoin)
	 */
	async confirm() {
		const variantId = this.state.selectedVariantId;
		if (!variantId) return;

		const modal = document.querySelector(this.selectors.modal);
		const confirmBtn = modal?.querySelector(this.selectors.confirm);
		if (confirmBtn) {
			confirmBtn.disabled = true;
			confirmBtn.classList.add('mm-loading');
		}

		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		try {
			// 1. Retirer un éventuel cadeau déjà présent (changement de choix)
			const removals = {};
			this.getEligibleVariants().forEach(id => {
				if (String(id) === String(variantId)) return; // garder celui qu'on (re)choisit
				const inCart = document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', id));
				if (inCart) removals[id] = 0;
			});

			if (Object.keys(removals).length > 0) {
				const removeBody = { updates: removals };
				if (sectionId) removeBody.sections = sectionId;
				await CartAPI.request('POST', 'cart/update.js', removeBody);
			}

			// 2. Ajouter le cadeau choisi (s'il n'est pas déjà présent)
			const alreadyInCart = document.querySelector(CART_CONFIG.selectors.cartItemById.replace('{{id}}', variantId));
			if (!alreadyInCart) {
				const formData = new FormData();
				formData.append('id', variantId);
				formData.append('quantity', '1');
				if (sectionId) formData.append('sections', sectionId);

				cartEmit('free-product-adding', { variantId });
				await CartAPI.request('POST', 'cart/add.js', formData);
			}

			cartEmit('free-products-updated', { variantId });
			this.close();
		} catch (error) {
			cartEmit('free-products-error', { error: error.message });
			cartLog('error', 'Free product selection failed', { error: error.message, variantId });
		} finally {
			if (confirmBtn) {
				confirmBtn.classList.remove('mm-loading');
				confirmBtn.disabled = false;
			}
			this.state.selectedVariantId = null;
		}
	},

	/**
	 * Bind unique par délégation (robuste aux refresh DOM du drawer)
	 */
	init() {
		if (this._bound) return;
		this._bound = true;

		document.addEventListener('click', (e) => {
			const openBtn = e.target.closest(this.selectors.open);
			if (openBtn) { e.preventDefault(); this.open(openBtn); return; }

			const closeBtn = e.target.closest(this.selectors.close);
			if (closeBtn) { e.preventDefault(); this.close(); return; }

			const variantBtn = e.target.closest(this.selectors.variant);
			if (variantBtn && !variantBtn.disabled) { e.preventDefault(); this.selectVariant(variantBtn); return; }

			const confirmBtn = e.target.closest(this.selectors.confirm);
			if (confirmBtn && !confirmBtn.disabled) { e.preventDefault(); this.confirm(); return; }
		});

		document.addEventListener('keydown', (e) => {
			if (e.key !== 'Escape') return;
			const modal = document.querySelector(this.selectors.modal);
			if (modal && modal.classList.contains('mm-open')) this.close();
		});
	}
};

window.FreeProductSelector = FreeProductSelector;

/*********************************************************************************************
 * 🔢 QUANTITY CONTROLS
 */
const debouncedUpdate = cartDebounce((lineIndex) => {
	const quantity = Cart.getQuantity(lineIndex);
	Cart.updateQuantity(lineIndex, quantity);
}, CART_CONFIG.debounceDelay);

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS
 */

/**
 * Increases product quantity by 1
 */
window.mmIncreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const maxValue = itemData?.maxQuantity;
	const max = (maxValue && !isNaN(parseInt(maxValue, 10))) ? parseInt(maxValue, 10) : Infinity;
	const newQty = current + 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		max,
		action: 'increase'
	};

	if (!cartEmit('before-quantity-increase', eventData, true)) return;
	
	if (newQty > max) {
		cartEmit('quantity-limit-reached', eventData);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-increased', { ...eventData, newQty });
	debouncedUpdate(lineIndex);
};

/**
 * Decreases product quantity by 1, removes if quantity would be 0
 */
window.mmDecreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const newQty = current - 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		action: 'decrease'
	};

	if (!cartEmit('before-quantity-decrease', eventData, true)) return;
	
	if (newQty < 1) {
		// If quantity would be less than 1, remove the item completely
		const removeEventData = {
			...itemData,
			oldQty: current,
			action: 'remove'
		};
		
		if (!cartEmit('before-item-remove', removeEventData, true)) return;
		
		Cart.setQuantity(lineIndex, 0);
		cartEmit('item-removed', removeEventData);
		debouncedUpdate(lineIndex);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-decreased', eventData);
	debouncedUpdate(lineIndex);
};

/**
 * Removes product completely from cart
 */
window.mmDestroyProductFromCart = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);

	const eventData = {
		...itemData,
		oldQty: current,
		action: 'destroy'
	};

	if (!cartEmit('before-item-remove', eventData, true)) return;

	Cart.setQuantity(lineIndex, 0);
	cartEmit('item-removed', eventData);
	debouncedUpdate(lineIndex);
};

/*********************************************************************************************
 * 🏗️ GLOBAL FUNCTIONS
 */
window.mmOpenCart = (triggerElement) => Cart.open(triggerElement);
window.mmCloseCart = () => Cart.close();
window.mmGetCart = () => Cart.get();
window.mmGetCartItemData = (lineIndex) => Cart.getItemData(lineIndex);

/*********************************************************************************************
 * 🏗️ UTILITY FUNCTIONS (MERGED ABOVE)
 */
window.updateCartCount = (value) => CartAPI.updateUI({ item_count: value });

// Adds loading state to checkout button
window.mmLoadThis = (element) => {
	if (element) {
		element.classList.add('mm-loading');
		cartEmit('checkout-loading', { element });
	}
};

// Cart UI Event Listeners
const initCartEventListeners = () => {
	// Close cart functionality
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-cart-close]')) {
			e.preventDefault();
			Cart.close();
		}
	});
};

/*********************************************************************************************
 * 🛒 Add to Cart Forms Handler
 */
window.initAddToCartForms = () => {
	document.querySelectorAll('[data-type="add-to-cart-form"]:not([data-cart-handler-attached])').forEach(form => {
		form.setAttribute('data-cart-handler-attached', 'true');
		form.addEventListener('submit', async (e) => {
			e.preventDefault();

			// Bouton reellement clique (plusieurs submit possibles), sinon le premier
			const submitButton = e.submitter || form.querySelector('[type="submit"]');

			// Prevent submission if button is disabled (out of stock)
			if (submitButton && submitButton.disabled) {
				cartLog('info', 'Add to cart blocked: product unavailable');
				return;
			}

			const formData = new FormData(form);

			if (!formData.get('id')) {
				cartLog('error', 'No variant ID found in form', { form });
				return;
			}
			
			// Add loading state
			if (submitButton) {
				// For product cards, add mm-loading to content wrapper
				const contentWrapper = submitButton.querySelector('.mm-product-card-btn-add-to-cart-btn-content');
				if (contentWrapper) {
					contentWrapper.classList.add('mm-loading');
				} else {
					submitButton.classList.add('mm-loading');
				}
				submitButton.disabled = true;
			}
			
			// Extract all form data including properties and options
			const extractedData = {
				variantId: formData.get('id'),
				quantity: parseInt(formData.get('quantity') || 1, 10),
				properties: {},
				options: {},
				formEntries: {}
			};
			
			// Collect all form entries
			for (const [key, value] of formData.entries()) {
				extractedData.formEntries[key] = value;
				
				// Extract properties (custom fields)
				if (key.startsWith('properties[')) {
					const propName = key.match(/properties\[(.+)\]/)?.[1];
					if (propName && value) {
						extractedData.properties[propName] = value;
					}
				}
				
				// Extract options (variant selections like size, color)
				if (key.startsWith('option')) {
					extractedData.options[key] = value;
				}
			}
			
			cartEmit('add-to-cart-start', extractedData);
			
			try {
				const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
				
				// Add sections for cart update if available
				if (sectionId) {
					formData.append('sections', sectionId);
				}
				
				const response = await CartAPI.request('POST', 'cart/add.js', formData);
				cartEmit('add-to-cart-success', {
					...extractedData,
					response: response
				});

				// Process free products (gift with purchase)
				await Cart.processFreeProducts(response);

				// Auto-open cart after successful add ; le focus reviendra au bouton
				// d'ajout a la fermeture (A11Y-21), sauf ajout depuis le tiroir lui-meme
				Cart.open(form.closest('[data-cart-wrapper]') ? null : submitButton);
				
			} catch (error) {
				Cart.announceError(error.userMessage, submitButton);
				cartEmit('add-to-cart-error', {
					...extractedData,
					error: error.message
				});
				cartLog('error', 'Add to cart failed', { 
					error: error.message, 
					...extractedData
				});
			} finally {
				// Remove loading state
				if (submitButton) {
					// For product cards, remove mm-loading from content wrapper
					const contentWrapper = submitButton.querySelector('.mm-product-card-btn-add-to-cart-btn-content');
					if (contentWrapper) {
						contentWrapper.classList.remove('mm-loading');
					} else {
						submitButton.classList.remove('mm-loading');
					}
					submitButton.disabled = false;
				}
			}
		});
	});
};

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
window.initCartQuantityListeners = function initCartQuantityListeners() {
	// Cart decrease buttons
	document.querySelectorAll('[data-cart-decrease]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartDecrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDecreaseProductQuantity(lineIndex);
			if (priceContainer) window.mmLoadThis(priceContainer);
		});
	});

	// Cart increase buttons
	document.querySelectorAll('[data-cart-increase]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartIncrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmIncreaseProductQuantity(lineIndex);
			if (priceContainer) window.mmLoadThis(priceContainer);
		});
	});

	// Cart remove buttons (delete item)
	document.querySelectorAll('[data-cart-remove]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartRemove;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDestroyProductFromCart(lineIndex);
			if (priceContainer) {
				window.mmLoadThis(priceContainer);
			} else {
				// For free products, load the button itself
				window.mmLoadThis(btn);
			}
		});
	});
}

/*********************************************************************************************
 * 🏆 CLUB DYNVEO - PRICE DISPLAY
 */

/**
 * Apply club or prescripteur pricing to cart items.
 *
 * Club        : tag client OU produit d'adhésion dans le panier.
 * Prescripteur: tag client uniquement (pas de produit d'adhésion).
 *
 * Les deux remises sont mutuellement exclusives — le prescripteur l'emporte,
 * conformément à mm-customer-status.liquid (source unique de vérité Liquid).
 */
async function applyClubPricing() {
	const clubProductId = window.clubDynveoProductId;
	const clubDiscountPercentage = window.clubDynveoDiscount || 15;
	const hasClubTag = window.hasClubTag || false;
	const prescDiscountPercentage = window.prescripteurDiscount || 15;
	const hasPrescripteurTag = window.hasPrescripteurTag || false;

	// Le club a besoin de son produit d'adhésion ; le prescripteur non.
	// On ne sort que si aucune des deux remises n'est exploitable.
	if (!clubProductId && !hasPrescripteurTag) {
		return;
	}

	try {
		// Check if club product is in cart (skip le fetch si pas de produit club)
		let clubItem = null;
		if (clubProductId) {
			const cartData = await fetch(window.Shopify.routes.root + 'cart.js').then(r => r.json());
			clubItem = cartData.items.find(item => item.id === parseInt(clubProductId));
		}

		// User should get club pricing if:
		// 1. User has club tag (already member) OR
		// 2. Club product is in cart (new member)
		const shouldApplyClubPricing = hasClubTag || !!clubItem;

		// Get all cart items with pricing data
		const priceWrappers = document.querySelectorAll('[data-item-product-id]');

		priceWrappers.forEach(wrapper => {
			const variantId = wrapper.dataset.itemVariantId;
			const originalPrice = parseInt(wrapper.dataset.itemOriginalPrice);
			const quantity = parseInt(wrapper.dataset.itemQuantity);
			const isInClubCollection = wrapper.dataset.itemInClubCollection === 'true';
			const isInPrescCollection = wrapper.dataset.itemInPrescripteurCollection === 'true';

			// Skip if this is the club product itself
			if (clubProductId && variantId === clubProductId.toString()) {
				return;
			}

			// Exclusivité : le prescripteur prime sur le club (cf. mm-customer-status)
			const applyPresc = hasPrescripteurTag && isInPrescCollection;
			const applyClub = !applyPresc && shouldApplyClubPricing && isInClubCollection;

			const clubLabel = wrapper.querySelector('[data-club-label]');
			const prescLabel = wrapper.querySelector('[data-prescripteur-label]');
			const priceElement = wrapper.querySelector('[data-final-line-price]');
			const compareAtPriceElement = wrapper.querySelector('.mm-dc-product-compare-at-price');

			// Aucune remise applicable : masquer les labels ET restaurer le prix
			// plein (cas du retrait de la carte club en cours de session).
			if (!applyPresc && !applyClub) {
				if (clubLabel) clubLabel.classList.add('mm-none');
				if (prescLabel) prescLabel.classList.add('mm-none');

				if (priceElement && priceElement.dataset.originalLinePrice) {
					const originalLinePrice = parseInt(priceElement.dataset.originalLinePrice);
					priceElement.textContent = formatMoney(originalLinePrice);
					priceElement.dataset.finalLinePrice = originalLinePrice;
					delete priceElement.dataset.originalLinePrice;
				}

				const staleComparePrice = wrapper.querySelector('[data-club-compare-price]');
				if (staleComparePrice) {
					staleComparePrice.remove();
				}
				return;
			}

			{
				// Apply pricing (prescripteur ou club selon l'exclusivité ci-dessus)
				const discountPercentage = applyPresc ? prescDiscountPercentage : clubDiscountPercentage;
				const discountMultiplier = (100 - discountPercentage) / 100;
				const clubPrice = Math.round(originalPrice * discountMultiplier);
				const clubLinePrice = clubPrice * quantity;

				// Show the matching label, hide the other
				if (clubLabel) {
					clubLabel.classList.toggle('mm-none', !applyClub);
				}
				if (prescLabel) {
					prescLabel.classList.toggle('mm-none', !applyPresc);
				}

				// Update price display
				if (priceElement) {
					// Store original price if not already stored
					if (!priceElement.dataset.originalLinePrice) {
						priceElement.dataset.originalLinePrice = priceElement.dataset.finalLinePrice || (originalPrice * quantity);
					}

					// Format club price using Shopify money format
					const formattedClubPrice = formatMoney(clubLinePrice);
					priceElement.textContent = formattedClubPrice;
					priceElement.dataset.finalLinePrice = clubLinePrice;
				}

				// Show original price as compare-at-price if not already showing a discount
				if (!compareAtPriceElement && priceElement) {
					const originalLinePrice = originalPrice * quantity;
					const formattedOriginalPrice = formatMoney(originalLinePrice);

					const comparePrice = document.createElement('p');
					comparePrice.className = 'mm-dc-product-compare-at-price mm-text-12-regular mm-gris-500';
					comparePrice.innerHTML = `<s>${formattedOriginalPrice}</s>`;
					comparePrice.dataset.clubComparePrice = 'true';

					priceElement.parentNode.insertBefore(comparePrice, priceElement);
				}
			}
		});

		cartEmit('club-pricing-updated', { clubActive: shouldApplyClubPricing || hasPrescripteurTag });

		// Apply club pricing to cross-sell items
		const applyCrossSellPricing = () => {
			const crossSellWrappers = document.querySelectorAll('[data-crosssell-product-id]');

			crossSellWrappers.forEach(wrapper => {
				const variantId = wrapper.dataset.crosssellVariantId;
				const originalPrice = parseInt(wrapper.dataset.crosssellOriginalPrice);
				const isInClubCollection = wrapper.dataset.crosssellInClubCollection === 'true';
				const isInPrescCollection = wrapper.dataset.crosssellInPrescripteurCollection === 'true';

				// Skip if this is the club product itself
				if (clubProductId && variantId === clubProductId.toString()) {
					return;
				}

				// Exclusivité : le prescripteur prime sur le club
				const applyPresc = hasPrescripteurTag && isInPrescCollection;
				const applyClub = !applyPresc && shouldApplyClubPricing && isInClubCollection;

				const clubLabel = wrapper.querySelector('[data-club-label]');
				const prescLabel = wrapper.querySelector('[data-prescripteur-label]');
				const priceElement = wrapper.querySelector('[data-crosssell-price]');

				// Skip si le produit n'est éligible à aucune des deux remises
				if (!applyPresc && !applyClub) {
					return;
				}

				{
					// Apply pricing (prescripteur ou club selon l'exclusivité)
					const discountPercentage = applyPresc ? prescDiscountPercentage : clubDiscountPercentage;
					const discountMultiplier = (100 - discountPercentage) / 100;
					const clubPrice = Math.round(originalPrice * discountMultiplier);

					// Show the matching label, hide the other
					if (clubLabel) {
						clubLabel.classList.toggle('mm-none', !applyClub);
					}
					if (prescLabel) {
						prescLabel.classList.toggle('mm-none', !applyPresc);
					}

					// Update price display
					if (priceElement) {
						const formattedClubPrice = formatMoney(clubPrice);
						priceElement.textContent = formattedClubPrice;
					}
				}
			});
		};

		// Apply cross-sell pricing
		applyCrossSellPricing();

	} catch (error) {
		console.error('🛒 [CLUB PRICING] Failed to apply club pricing', { error: error.message });
	}
}

/**
 * Format money: 10€ (no decimals for integers) or 19,90€ (2 decimals otherwise), no space before €
 */
function formatMoney(cents) {
	const amount = cents / 100;

	// Use Shopify.formatMoney if available
	if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
		// For integer amounts, use format without decimals
		if (amount % 1 === 0) {
			return Shopify.formatMoney(cents, '{{amount_no_decimals_with_comma_separator}}€');
		}
		// For decimal amounts, use format with 2 decimals
		return Shopify.formatMoney(cents, '{{amount_with_comma_separator}}€');
	}

	// Fallback: manual formatting
	if (amount % 1 === 0) {
		// Integer: no decimals (10€)
		return amount.toString() + '€';
	}
	// Decimal: 2 decimals (19,90€)
	return amount.toFixed(2).replace('.', ',') + '€';
}

/*********************************************************************************************
 * 🏆 CLUB BENEFITS POPUP & TOGGLE
 */
function initClubBenefitsPopup() {
	const trigger = document.querySelector('[data-club-benefits-trigger]');
	const popup = document.querySelector('[data-club-benefits-popup]');

	if (trigger && popup) {
		const textShow = trigger.dataset.textShow || 'Voir les avantages';
		const textHide = trigger.dataset.textHide || 'Masquer les avantages';

		const triggerTextEl = trigger.querySelector('span');

		trigger.addEventListener('click', (e) => {
			e.preventDefault();
			const isHidden = popup.classList.toggle('mm-none');

			// Change button text based on popup state
			if (triggerTextEl) triggerTextEl.textContent = isHidden ? textShow : textHide;
			trigger.classList.toggle('is-active', !isHidden);
		});

		// Close popup when clicking outside
		document.addEventListener('click', (e) => {
			if (!trigger.contains(e.target) && !popup.contains(e.target)) {
				if (!popup.classList.contains('mm-none')) {
					popup.classList.add('mm-none');
					if (triggerTextEl) triggerTextEl.textContent = textShow;
					trigger.classList.remove('is-active');
				}
			}
		});
	}
}

async function initClubToggle() {
	const toggle = document.querySelector('.mm-dc-club-toggle');

	if (!toggle) {
		return;
	}

	const clubProductId = window.clubDynveoProductId;

	if (!clubProductId) {
		return;
	}

	// Function to check if club product is in cart
	const isClubProductInCart = async () => {
		try {
			const cartData = await CartAPI.request('GET', 'cart.js');
			const clubItem = cartData.items.find(item => item.id === parseInt(clubProductId));
			return clubItem ? true : false;
		} catch (error) {
			console.error('🛒 [CLUB] Failed to check cart state', { error: error.message });
			return false;
		}
	};

	// Check initial state and sync toggle
	const inCart = await isClubProductInCart();
	toggle.checked = inCart;

	// Remove existing event listener if any
	const newToggle = toggle.cloneNode(true);
	toggle.parentNode.replaceChild(newToggle, toggle);

	newToggle.addEventListener('change', async (e) => {
		const isChecked = e.target.checked;
		const toggleContainer = e.target.closest('.mm-dc-club-toggle-container');

		if (isChecked) {
			// User wants to add club product
			try {
				if (toggleContainer) {
					toggleContainer.style.opacity = '0.6';
					toggleContainer.style.pointerEvents = 'none';
				}

				// Verify if product is already in cart
				const alreadyInCart = await isClubProductInCart();

				if (alreadyInCart) {
					if (toggleContainer) {
						toggleContainer.style.opacity = '1';
						toggleContainer.style.pointerEvents = 'auto';
					}
					return;
				}

				const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
				const formData = new FormData();
				formData.append('id', clubProductId);
				formData.append('quantity', '1');

				if (sectionId) {
					formData.append('sections', sectionId);
				}

				cartEmit('club-add-start', { clubProductId });

				const response = await CartAPI.request('POST', 'cart/add.js', formData);

				cartEmit('club-add-success', { clubProductId, response });

				if (toggleContainer) {
					toggleContainer.style.opacity = '1';
					toggleContainer.style.pointerEvents = 'auto';
				}

			} catch (error) {
				console.error('🛒 [CLUB] Failed to add club product', { error: error.message });
				cartEmit('club-add-error', { clubProductId, error: error.message });

				// Uncheck toggle on error
				e.target.checked = false;

				if (toggleContainer) {
					toggleContainer.style.opacity = '1';
					toggleContainer.style.pointerEvents = 'auto';
				}
			}
		} else {
			// User wants to remove club product
			try {
				if (toggleContainer) {
					toggleContainer.style.opacity = '0.6';
					toggleContainer.style.pointerEvents = 'none';
				}

				// Find the line item with this variant ID in the current cart
				const cartData = await fetch(window.Shopify.routes.root + 'cart.js').then(r => r.json());
				const clubItem = cartData.items.find(item => item.id === parseInt(clubProductId));

				if (clubItem) {
					const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
					const requestBody = {
						updates: {
							[clubProductId]: 0
						}
					};

					if (sectionId) {
						requestBody.sections = sectionId;
					}

					cartEmit('club-remove-start', { clubProductId });
					await CartAPI.request('POST', 'cart/update.js', requestBody);
					cartEmit('club-remove-success', { clubProductId });
				}

				if (toggleContainer) {
					toggleContainer.style.opacity = '1';
					toggleContainer.style.pointerEvents = 'auto';
				}

			} catch (error) {
				console.error('🛒 [CLUB] Failed to remove club product', { error: error.message });
				cartEmit('club-remove-error', { clubProductId, error: error.message });

				// Re-check toggle on error
				e.target.checked = true;

				if (toggleContainer) {
					toggleContainer.style.opacity = '1';
					toggleContainer.style.pointerEvents = 'auto';
				}
			}
		}
	});
}

/*********************************************************************************************
 * 💰 PROMO CODE MANAGEMENT
 */
function initPromoCode() {
	const applyBtn = document.querySelector('[data-action="apply-discount"]');
	const discountInput = document.querySelector('input[data-name="discount"]');

	cartLog('info', '[PROMO] Initializing promo code', { applyBtn: !!applyBtn, discountInput: !!discountInput });

	if (applyBtn && discountInput) {
		applyBtn.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			cartLog('info', '[PROMO] Apply discount button clicked');
			applyDiscount();
		});
		discountInput.addEventListener('keypress', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
				cartLog('info', '[PROMO] Enter key pressed in discount input');
				applyDiscount();
			}
		});
		// Hide error when user starts typing
		discountInput.addEventListener('input', () => {
			hidePromoError();
		});
		cartLog('info', '[PROMO] Promo code listeners attached successfully');
	}
}

async function applyDiscount() {
	cartLog('info', '[PROMO] applyDiscount function called');

	const input = document.querySelector('input[data-name="discount"]');
	const button = document.querySelector('[data-action="apply-discount"]');

	if (!input || !button) {
		console.error('🛒 [PROMO] Input or button not found', { input: !!input, button: !!button });
		return;
	}

	const code = input.value.trim();
	cartLog('info', '[PROMO] Discount code entered', { code });

	if (!code) {
		console.warn('🛒 [PROMO] Empty discount code');
		return;
	}

	// Disable button and show loading state
	button.disabled = true;
	button.classList.add('mm-loading');

	try {
		// Get cart state before applying discount
		cartLog('info', '[PROMO] Getting cart state before discount...');
		const cartBefore = await CartAPI.request('GET', 'cart.js');
		const discountsBefore = cartBefore.discount_codes || [];
		cartLog('info', '[PROMO] Cart before', {
			total: cartBefore.total_price,
			discounts: discountsBefore.length,
			discountsList: discountsBefore
		});

		// Check if this exact code is already applied
		// Note: discount_codes have 'code' property, not 'title'
		const codeAlreadyApplied = discountsBefore.some(d => {
			const discountCode = d.code || d.title;
			return discountCode && discountCode.toLowerCase() === code.toLowerCase();
		});
		if (codeAlreadyApplied) {
			const errorMsg = input.dataset.errorAlreadyApplied || 'Un code promo est déjà appliqué';
			showPromoError(`Ce code est déjà appliqué`);
			cartLog('info', '[PROMO] This code is already applied', { code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if another discount is already applied (Shopify allows only one)
		if (discountsBefore.length > 0) {
			const existingDiscount = discountsBefore[0].code || discountsBefore[0].title;
			const errorMsg = input.dataset.errorAlreadyApplied || 'Un code promo est déjà appliqué';
			showPromoError(`${errorMsg} (${existingDiscount}). Retirez-le d'abord.`);
			cartLog('info', '[PROMO] Another discount already applied', { existing: existingDiscount, attempted: code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if cart is empty
		if (cartBefore.item_count === 0) {
			const errorMsg = input.dataset.errorEmptyCart || 'Votre panier est vide';
			showPromoError(errorMsg);
			cartLog('info', '[PROMO] Cart is empty');
			input.value = ''; // Clear invalid code
			return;
		}

		// Apply discount via cart/update.js API
		cartLog('info', '[PROMO] Applying discount via API...', { code });

		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		const updatePayload = {
			discount: code
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		if (sectionId) await CartAPI.ensureSections(updateData, sectionId);
		cartLog('info', '[PROMO] Cart updated', {
			updateData,
			discount_codes: updateData.discount_codes,
			discount_applications: updateData.discount_applications,
			cart_level_discount_applications: updateData.cart_level_discount_applications,
			total_discount: updateData.total_discount
		});

		// Verify if discount was applied - check multiple sources
		// 1. Check discount_codes (codes entered by user) - must be applicable: true
		// 2. Check discount_applications (all types of discounts)
		// 3. Check cart_level_discount_applications (cart-level discounts)
		// 4. Check if total_discount > 0

		// Check in discount_codes (most reliable for user-entered codes)
		const codeInDiscountCodes = (updateData.discount_codes || []).some(d => {
			return d.code && d.code.toLowerCase() === code.toLowerCase() && d.applicable === true;
		});

		// Check in discount_applications
		const allDiscountApps = [
			...(updateData.discount_applications || []),
			...(updateData.cart_level_discount_applications || [])
		];

		const codeInApps = allDiscountApps.some(d => {
			const discountTitle = (d.title || d.code || '').toLowerCase();
			return discountTitle === code.toLowerCase();
		});

		// Check if total_discount increased (indicates discount was applied)
		const hasDiscount = updateData.total_discount > 0;

		cartLog('info', '[PROMO] Discount verification', {
			codeInDiscountCodes,
			codeInApps,
			hasDiscount,
			total_discount: updateData.total_discount
		});

		// Code is valid if it's in discount_codes with applicable:true OR in apps OR has discount
		const codeApplied = codeInDiscountCodes || codeInApps || hasDiscount;

		if (!codeApplied) {
			// Discount was not applied - code is invalid or not applicable
			console.warn('🛒 [PROMO] Discount was not applied by Shopify', { code });

			// IMPORTANT: Force removal of invalid code from cart
			// Shopify keeps invalid codes in discount_codes[] even if not applied
			const cleanupPayload = {
				discount: '' // Empty string removes the discount
			};

			if (sectionId) {
				cleanupPayload.sections = sectionId;
			}

			try {
				await fetch(window.Shopify.routes.root + 'cart/update.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(cleanupPayload)
				});
				cartLog('info', '[PROMO] Invalid code cleaned from cart', { code });
			} catch (cleanupError) {
				console.error('🛒 [PROMO] Failed to clean invalid code', { error: cleanupError.message });
			}

			const errorMsg = input.dataset.errorNotApplicable || 'Ce code promo ne peut pas être appliqué à votre panier';
			showPromoError(errorMsg);
			input.value = ''; // Clear invalid code
			return;
		}

		// Discount applied successfully, refresh UI
		cartLog('info', '[PROMO] Discount applied successfully');

		// Sections deja completees par ensureSections (requete ci-dessus)
		if (updateData.sections?.[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		}

		// Clear input
		input.value = '';

		// Emit success event
		cartEmit('discount-applied', { code });
		cartLog('info', '[PROMO] Discount applied and cart refreshed');

	} catch (error) {
		console.error('🛒 [PROMO] Failed to apply discount', { error: error.message, stack: error.stack });
		showPromoError('Erreur lors de l\'application du code');
		input.value = ''; // Clear invalid code on error
	} finally {
		// Reset button state
		button.disabled = false;
		button.classList.remove('mm-loading');
	}
}

function showPromoError(message) {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.textContent = message;
		errorElement.classList.remove('mm-none');
	}

	if (input) {
		input.classList.add('error');
	}

	// Auto-hide after 5 seconds
	setTimeout(() => {
		hidePromoError();
	}, 5000);
}

function hidePromoError() {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.classList.add('mm-none');
		errorElement.textContent = '';
	}

	if (input) {
		input.classList.remove('error');
	}
}

window.removeDiscount = async function(code) {
	cartLog('info', '[PROMO] Removing discount', { code });

	const discountButton = document.querySelector(`[data-discount-code="${code.toLowerCase()}"] [data-discount-remove]`);

	if (discountButton) {
		// Add loading state
		discountButton.disabled = true;
		discountButton.style.opacity = '0.5';
	}

	try {
		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		// Remove discount via cart/update.js API with empty discount parameter
		cartLog('info', '[PROMO] Removing discount via API...', { code });

		const updatePayload = {
			discount: ''  // Empty string removes the discount
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		if (sectionId) await CartAPI.ensureSections(updateData, sectionId);
		cartLog('info', '[PROMO] Discount removal response', { updateData });

		// Verify discount was removed
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const discountStillPresent = cartAfter.cart_level_discount_applications?.some(d => {
			const discountTitle = d.title || d.code;
			return discountTitle && discountTitle.toLowerCase() === code.toLowerCase();
		});

		if (discountStillPresent) {
			console.error('🛒 [PROMO] Discount was not removed', { code });
			throw new Error('Discount removal failed');
		}

		cartLog('info', '[PROMO] Discount removed successfully', { code });

		// Refresh UI : sections deja completees par ensureSections (requete ci-dessus)
		if (updateData.sections?.[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		}

		// Emit success event
		cartEmit('discount-removed', { code });
		cartLog('info', '[PROMO] Discount removed and cart refreshed');

	} catch (error) {
		console.error('🛒 [PROMO] Failed to remove discount', { error: error.message, stack: error.stack });

		// Reset button state on error
		if (discountButton) {
			discountButton.disabled = false;
			discountButton.style.opacity = '1';
		}
	}
};

/**
 * Add loading state to checkout button on click
 */
function initCheckoutButton() {
	const checkoutBtn = document.querySelector('[data-checkout-btn] .mm-dc-bottom-cta');
	if (checkoutBtn) {
		checkoutBtn.addEventListener('click', () => {
			checkoutBtn.classList.add('mm-loading');
		});
	}
}

/*********************************************************************************************
 * 🛍️ CROSS SELL RECOMMENDATIONS
 */

// Cache for cross-sell cards HTML
const crossSellCache = {
	html: null,
	productIdsKey: null
};

// Lock to prevent concurrent cross-sell fetches
let crossSellFetching = false;

/**
 * Load cross-sell product recommendations based on cart items
 */
async function initCrossSellRecommendations() {
	const container = document.querySelector('.mm-cart-smart-cross-sell-container');
	if (!container) return;

	// Prevent concurrent executions
	if (crossSellFetching) return;
	crossSellFetching = true;

	const loader = container.querySelector('.mm-cart-smart-cross-sell-loader-container');

	// Clear existing cross-sell products before fetching new ones
	container.querySelectorAll('.mm-dc-cross-product').forEach(el => el.remove());

	// Get last 4 cart items (most recently added first)
	const items = [...document.querySelectorAll('.mm-dc-products > .mm-dc-product')].reverse().slice(0, 4);
	if (items.length === 0) {
		container.innerHTML = '';
		crossSellFetching = false;
		return;
	}

	// Unique key based on cart product IDs
	const productIdsKey = items.map(i => i.dataset.productId).join('-');

	// Check cache
	if (crossSellCache.productIdsKey === productIdsKey && crossSellCache.html) {
		if (loader) loader.classList.add('mm-none');
		container.insertAdjacentHTML('beforeend', crossSellCache.html);
		if (typeof window.initAddToCartForms === 'function') {
			window.initAddToCartForms();
		}
		crossSellFetching = false;
		return;
	}

	// Fetch recommendations for each item (parallel)
	const recoFetches = items.map(item => {
		const pid = item.dataset.productId;
		return fetch(`/recommendations/products.json?product_id=${pid}&intent=related`).then(r => r.json());
	});

	const recoResponses = await Promise.all(recoFetches);

	// Select up to 2 unique handles per item, max 5 total
	const MAX_CROSS_SELL = 5;
	const uniqueHandles = new Set();
	const handles = [];

	for (const reco of recoResponses) {
		if (handles.length >= MAX_CROSS_SELL) break;
		let count = 0;
		for (const p of reco.products) {
			if (handles.length >= MAX_CROSS_SELL) break;
			if (!uniqueHandles.has(p.handle)) {
				uniqueHandles.add(p.handle);
				handles.push(p.handle);
				count++;
			}
			if (count >= 2) break;
		}
	}

	if (handles.length === 0) {
		if (loader) loader.classList.add('mm-none');
		crossSellFetching = false;
		return;
	}

	// Fetch HTML for product cards
	const htmlFetches = handles.map(handle => {
		return fetch(`/products/${encodeURIComponent(handle)}?view=card`).then(r => r.text());
	});

	const htmlResponses = await Promise.all(htmlFetches);

	// Build final HTML
	let finalHTML = '';
	for (const html of htmlResponses) {
		const div = document.createElement('div');
		div.innerHTML = html;
		const card = div.querySelector('.mm-dc-cross-product');
		if (card) {
			finalHTML += card.outerHTML;
		}
	}

	// Hide loader and insert content
	if (loader) loader.classList.add('mm-none');
	container.insertAdjacentHTML('beforeend', finalHTML);

	// Initialize add to cart forms on new elements
	if (typeof window.initAddToCartForms === 'function') {
		window.initAddToCartForms();
	}

	// Save to cache
	crossSellCache.html = finalHTML;
	crossSellCache.productIdsKey = productIdsKey;

	// Release lock
	crossSellFetching = false;
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
const initCart = () => {
	cartEmit('initializing');
	// Poses avant la reponse de /cart.js : si l'appel echoue ou tarde, le drawer
	// se ferme quand meme et l'ajout au panier ne part pas en POST natif vers /cart
	initCartEventListeners();
	window.initAddToCartForms();
	initCartQuantityListeners();
	initClubBenefitsPopup();
	initClubToggle();
	initPromoCode();
	initCheckoutButton();
	FreeProductSelector.init();

	Cart.get()
		.then(async data => {
			cartEmit('initialized', { itemCount: data.item_count, totalPrice: data.total_price });

			// Apply club pricing on initial load
			await applyClubPricing();

			// Traiter les produits offerts si un palier est déjà atteint au chargement
			await Cart.initFreeProducts();

			// Check if cart should be reopened after discount operation
			if (sessionStorage.getItem('mm_cart_should_reopen') === 'true') {
				cartLog('info', '[PROMO] Reopening cart after discount operation');
				sessionStorage.removeItem('mm_cart_should_reopen');

				// Small delay to ensure page is fully loaded
				setTimeout(() => {
					Cart.open(document.querySelector('.mm-header-cart-container'));
				}, 200);
			}
		})
		.catch(error => {
			cartLog('error', 'Cart initialization failed', { error: error.message });
			cartEmit('initialization-error', { error: error.message });
		});
};

// readyState : le script peut arriver apres DOMContentLoaded (injection par une app)
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCart);
} else {
	initCart();
}

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (CART_CONFIG.isDevelopment) {
	['opened', 'closed', 'quantity-increased', 'quantity-decreased', 'free-product-adding', 'count-updated', 'add-to-cart-start', 'add-to-cart-success', 'add-to-cart-error'].forEach(event => {
		document.addEventListener(`cart:${event}`, e =>
			console.log(`🛒 [CART] ${event}:`, e.detail)
		);
	});
}
